import { Grid, Text, ColorPicker, Box } from "@mantine/core";
import { useForm, yupResolver } from "@mantine/form";
import { memo, useEffect } from "react";
import {
  OrganizationBrandingInput,
  useUpdateOrganizationBrandingMutation,
} from "src/generated/graphql";
import { object, string } from "yup";
import TextInput from "src/components/TextInput/TextInput";
import { EMPLOYEE_ROLE_OBJ, MESSAGES } from "src/utils/constants";
import { useDisclosure } from "@mantine/hooks";
import FormCard from "src/components/FormCard";
import { IOrgProps } from "../Detail";
import BrandingPreview from "./BrandingPreview";
import useWritePermission from "src/hooks/usePermission";

type IFormProps = OrganizationBrandingInput;

const colorPickerStyle = {
  saturation: { width: 218, height: 183 },
  slider: { width: 218 },
};

const brandingInitialValues = {
  brandingLogoImg: "",
  primaryColor: "",
  secondaryColor: "",
};

export const OrganizationBrandingFormSchema = object().shape({
  // accept empty string or hex string
  primaryColor: string()
    .nullable()
    .matches(/^(#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3}))?$/, "Invalid Hex Code"),
  secondaryColor: string()
    .nullable()
    .matches(/^(#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3}))?$/, "Invalid Hex Code"),
});

const CustomBranding = ({ refetch, organizationOne }: IOrgProps) => {
  const [isFormActive, { toggle: toggleFormActive }] = useDisclosure(true);
  const isAllowWrite = useWritePermission(EMPLOYEE_ROLE_OBJ.orgSettings);
  const form = useForm<IFormProps>({
    initialValues: brandingInitialValues,
    validate: yupResolver(OrganizationBrandingFormSchema),
  });
  const isFormUpdated = form.isDirty() && form.isTouched();

  useEffect(() => {
    resetFormState();
  }, [organizationOne]);

  const resetFormState = () => {
    if (!organizationOne?.organizationBranding) {
      form.setValues(brandingInitialValues);
      form.resetDirty(brandingInitialValues);
      return;
    }
    let { brandingLogoImg, primaryColor, secondaryColor } =
      organizationOne?.organizationBranding;
    const obj = {
      brandingLogoImg: brandingLogoImg || "",
      primaryColor: primaryColor || "",
      secondaryColor: secondaryColor || "",
    };
    form.setValues(obj);
    form.resetDirty(obj);
  };

  const [updateBranding, { loading: updateLoading }] =
    useUpdateOrganizationBrandingMutation({
      onCompleted() {
        refetch();
        toggleFormActive();
        form.resetDirty();
      },
    });

  const handleUpdate = async (values: IFormProps) => {
    organizationOne?.organizationBranding?.id &&
      updateBranding({
        variables: {
          data: values,
          updateOrganizationBrandingId: organizationOne.organizationBranding.id,
        },
      });
  };

  const handleEdit = () => {
    toggleFormActive();
  };

  const handleClick = () => {
    resetFormState();
    toggleFormActive();
  };

  return (
    <FormCard
      title={"Branding"}
      loading={updateLoading}
      bannerContent={isFormUpdated ? MESSAGES.UNSAVED_CHANGES : ""}
      onCancel={handleClick}
      onEdit={isAllowWrite ? handleEdit : undefined}
      onSave={form.onSubmit((values) => handleUpdate(values))}
      isFormActive={isFormActive}
    >
      <Grid justify="center" align="center">
        {/* <Grid.Col sm={4} xs={12}>
          <Box sx={{ width: 260, margin: "auto" }}>
            <ImageUpload
              disabled={isFormActive}
              height="260px"
              maxWidth="100%"
              onReadSuccess={(b64) =>
                form.setFieldValue("brandingLogoImg", b64)
              }
              src={form.values.brandingLogoImg || ""}
              alt="company Image"
            />
            <Text fw={500} size={16} align="center" pt={5}>
              Company Logo
            </Text>
          </Box>
        </Grid.Col> */}

        <Grid.Col sm={4} xs={12}>
          <Box sx={{ width: 235, margin: "auto" }}>
            <ColorPicker
              sx={{ pointerEvents: isFormActive ? "none" : "initial" }}
              styles={colorPickerStyle}
              {...form.getInputProps("primaryColor")}
            />
            <TextInput
              mt={"sm"}
              maxLength={7}
              label="#HexCode"
              disabled={isFormActive}
              {...form.getInputProps("primaryColor")}
            />
            <Text fw={500} size={16} align="center" pt={5}>
              Primary Color
            </Text>
          </Box>
        </Grid.Col>

        <Grid.Col sm={4} xs={12}>
          <Box sx={{ width: 235, margin: "auto" }}>
            <ColorPicker
              sx={{ pointerEvents: isFormActive ? "none" : "initial" }}
              styles={colorPickerStyle}
              {...form.getInputProps("secondaryColor")}
            />
            <TextInput
              mt={"sm"}
              maxLength={7}
              label="#HexCode"
              disabled={isFormActive}
              {...form.getInputProps("secondaryColor")}
            />
            <Text fw={500} size={16} align="center" pt={5}>
              Secondary Color
            </Text>
          </Box>
        </Grid.Col>
      </Grid>
      <BrandingPreview
        primaryColor={
          form.values.primaryColor ||
          organizationOne?.organizationBranding?.primaryColor ||
          "#4d4d4d"
        }
        secondaryColor={
          form.values.secondaryColor ||
          organizationOne?.organizationBranding?.secondaryColor ||
          "#d1d1d1"
        }
      />
    </FormCard>
  );
};

export default memo(CustomBranding);
